import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, STATUS_META } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
  doc_number: "", pr_date: "", cost_center: "", department: "PURLOG",
  product_no: "", description: "", doc_qty: 1, qty_supply: 0, qty_os: 0, uom: "PCS",
  pr_number: "", po_number: "", po_share_date: "",
  aging_pr_quot: 0, aging_pr_po: 0, aging_po_gr: 0, aging_pr_gr: 0,
  supplier: "", po_price_unit: 0, total_po_price: 0,
  date_in_warehouse: "", status_key: "WAITING_QUOT", status_supply: "OPEN",
  remarks: "", remarks_order: "",
};

export const POForm = ({ open, onOpenChange, initial, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) setF({ ...EMPTY, ...initial });
    else setF(EMPTY);
  }, [initial, open]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      if (initial?.id) {
        await api.put(`/purchases/${initial.id}`, f);
        toast.success("PO updated successfully");
      } else {
        await api.post("/purchases", f);
        toast.success("PO added successfully");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error("Failed to save PO");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-[#111D38] border-slate-700 text-slate-100" data-testid="po-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-100 text-xl">{initial?.id ? "Edit Purchase Order" : "Add Purchase Order"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[65vh] overflow-y-auto pr-2">
          <Field label="Doc. Number"><Input data-testid="input-doc-number" value={f.doc_number} onChange={(e) => set("doc_number", e.target.value)} className="bg-[#0B132B] border-slate-700" /></Field>
          <Field label="PR Date"><Input type="date" data-testid="input-pr-date" value={f.pr_date?.slice(0,10) || ""} onChange={(e) => set("pr_date", e.target.value)} className="bg-[#0B132B] border-slate-700" /></Field>
          <Field label="Cost Center"><Input data-testid="input-cost-center" value={f.cost_center} onChange={(e) => set("cost_center", e.target.value)} className="bg-[#0B132B] border-slate-700" /></Field>
          <Field label="Department">
            <Select value={f.department} onValueChange={(v) => set("department", v)}>
              <SelectTrigger data-testid="select-department" className="bg-[#0B132B] border-slate-700"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1C2541] border-slate-700 text-slate-100">
                {["PURLOG","PLANT","HRGA & IT","SHE","ENGINEERING","PRODUKSI"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Product No."><Input data-testid="input-product-no" value={f.product_no} onChange={(e) => set("product_no", e.target.value)} className="bg-[#0B132B] border-slate-700" /></Field>
          <Field label="UoM"><Input data-testid="input-uom" value={f.uom} onChange={(e) => set("uom", e.target.value)} className="bg-[#0B132B] border-slate-700" /></Field>
          <div className="sm:col-span-2"><Field label="Description"><Textarea data-testid="input-description" value={f.description} onChange={(e) => set("description", e.target.value)} className="bg-[#0B132B] border-slate-700" rows={2} /></Field></div>
          <Field label="Qty"><Input type="number" data-testid="input-doc-qty" value={f.doc_qty} onChange={(e) => set("doc_qty", Number(e.target.value))} className="bg-[#0B132B] border-slate-700" /></Field>
          <Field label="Qty Supply"><Input type="number" data-testid="input-qty-supply" value={f.qty_supply} onChange={(e) => set("qty_supply", Number(e.target.value))} className="bg-[#0B132B] border-slate-700" /></Field>
          <Field label="PR Number"><Input data-testid="input-pr-number" value={f.pr_number} onChange={(e) => set("pr_number", e.target.value)} className="bg-[#0B132B] border-slate-700" /></Field>
          <Field label="PO Number"><Input data-testid="input-po-number" value={f.po_number} onChange={(e) => set("po_number", e.target.value)} className="bg-[#0B132B] border-slate-700" /></Field>
          <Field label="Supplier"><Input data-testid="input-supplier" value={f.supplier} onChange={(e) => set("supplier", e.target.value)} className="bg-[#0B132B] border-slate-700" /></Field>
          <Field label="Price per Unit (IDR)"><Input type="number" data-testid="input-price-unit" value={f.po_price_unit} onChange={(e) => set("po_price_unit", Number(e.target.value))} className="bg-[#0B132B] border-slate-700" /></Field>
          <Field label="Total PO Price (IDR)"><Input type="number" data-testid="input-total-price" value={f.total_po_price} onChange={(e) => set("total_po_price", Number(e.target.value))} className="bg-[#0B132B] border-slate-700" /></Field>
          <Field label="Lead Time PR-GR (days)"><Input type="number" data-testid="input-lead-time" value={f.aging_pr_gr} onChange={(e) => set("aging_pr_gr", Number(e.target.value))} className="bg-[#0B132B] border-slate-700" /></Field>
          <Field label="Status">
            <Select value={f.status_key} onValueChange={(v) => set("status_key", v)}>
              <SelectTrigger data-testid="select-status" className="bg-[#0B132B] border-slate-700"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1C2541] border-slate-700 text-slate-100">
                {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Status Supply">
            <Select value={f.status_supply} onValueChange={(v) => set("status_supply", v)}>
              <SelectTrigger data-testid="select-status-supply" className="bg-[#0B132B] border-slate-700"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1C2541] border-slate-700 text-slate-100">
                <SelectItem value="OPEN">OPEN</SelectItem>
                <SelectItem value="CLOSE">CLOSE</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div className="sm:col-span-2"><Field label="Remarks Order"><Textarea data-testid="input-remarks-order" value={f.remarks_order} onChange={(e) => set("remarks_order", e.target.value)} className="bg-[#0B132B] border-slate-700" rows={2} /></Field></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="btn-cancel" className="text-slate-300 hover:text-slate-100">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="btn-save-po" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</Label>
    {children}
  </div>
);
